// components/FactHistorySheet.tsx — Chunk SYS-W: a row's history, opened
// from the Systems screen. Desktop: rises over the app like OutcomeSheet;
// phone: covers the screen (the spec's own "phone: full screen"), via
// FactHistorySheet.css's media query — no second component.
import { useEffect, useState } from 'preact/hooks';
import { ApiError, getFactHistory, type FactHistoryEventApi, type SystemSection } from '../lib/api';
import './FactHistorySheet.css';

export interface FactHistorySheetProps {
  tenantId: string;
  section: SystemSection;
  key_: string;
  label: string;
  onClose: () => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; events: FactHistoryEventApi[] };

function when(at: string): string {
  return new Date(at).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function FactHistorySheet({ tenantId, section, key_, label, onClose }: FactHistorySheetProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    getFactHistory(tenantId, section, key_)
      .then((events) => {
        if (!cancelled) setLoad({ phase: 'ready', events });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? (err.serverMessage ?? err.message) : 'this history could not be read',
        });
      });
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [tenantId, section, key_, onClose]);

  return (
    <div class="fh-sheet" role="dialog" aria-label={`${label} history`}>
      <button type="button" class="fh-sheet__scrim" aria-label="close" onClick={onClose} />
      <div class="fh-sheet__box">
        <div class="fh-sheet__grip" aria-hidden="true" />
        <div class="fh-sheet__header">
          <h2 class="fh-sheet__title">{label}</h2>
          <button type="button" class="fh-sheet__close" onClick={onClose}>
            Close
          </button>
        </div>
        {load.phase === 'loading' ? (
          <div class="fh-sheet__ghost" aria-hidden="true">
            <div class="fh-sheet__ghost-line" />
            <div class="fh-sheet__ghost-line" />
          </div>
        ) : load.phase === 'error' ? (
          <div class="fh-sheet__say">{load.message}</div>
        ) : load.events.length === 0 ? (
          <div class="fh-sheet__ghost" aria-hidden="true">
            <div class="fh-sheet__ghost-line" />
          </div>
        ) : (
          <div class="fh-sheet__list">
            {load.events.map((ev, i) => (
              <div class="fh-sheet__event" key={`${ev.at}-${i}`}>
                <div class="fh-sheet__event-top">
                  <span class="fh-sheet__event-state">{ev.state.replace(/_/g, ' ')}</span>
                  <span class="fh-sheet__event-at">{when(ev.at)}</span>
                </div>
                {ev.detail ? <div class="fh-sheet__event-detail">{ev.detail}</div> : null}
                <div class="fh-sheet__event-by">reported by {ev.reportedBy}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
